import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { ForeignKeyMultiSelect } from './ForeignKeyMultiSelect';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue
} from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const GET_MODEL_FIELDS = gql`
  query GetModelFields($model: String!) {
    modelFields(model: $model) {
      edges {
        node {
          id
          name
          fieldDescription
          ttype
          relation
          selectionIds {
            value
            name
          }
        }
      }
    }
    relatedModelFields(model: $model) {
      fieldPath
      fieldName
      fieldLabel
      fieldType
      relationName
      relationLabel
      relatedModel
      selectionOptions
    }
  }
`;

interface Field {
  fieldDescription: string;
  id: string;
  isRelated?: boolean;
  name: string;
  relation?: string;
  relationLabel?: string;
  selectionIds?: { name: string; value: string }[];
  ttype: string;
}

interface FilterBuilderProps {
  onChange: (value: string) => void;
  targetModel: 'account' | 'contact';
  value: string; // JSON domain string
}

interface FilterCondition {
  field: string;
  id: string;
  operator: string;
  value: string;
}

interface FilterGroup {
  conditions: FilterCondition[];
  id: string;
}

interface RelatedField {
  fieldLabel: string;
  fieldName: string;
  fieldPath: string;
  fieldType: string;
  relatedModel: string;
  relationLabel: string;
  relationName: string;
  selectionOptions?: string[];
}

// Utility to extract numeric ID from GraphQL global ID
// GraphQL IDs are base64 encoded strings like "SHJFbXBsb3llZU5vZGU6MQ=="
// When decoded, they become "ModelName:123" format
function extractNumericId(globalId: string): number {
  // Try to parse as integer first (if it's already numeric)
  const numericId = parseInt(globalId, 10);
  if (!isNaN(numericId)) {
    return numericId;
  }

  // Try to decode base64 if it looks like a base64 string
  try {
    // Check if it's a base64-like string (contains only base64 characters and ends with = or ==)
    if (/^[A-Za-z0-9+/]+={0,2}$/.test(globalId) && globalId.length > 10) {
      const decoded = atob(globalId);
      // Decoded format is usually "ModelName:123"
      const parts = decoded.split(':');
      if (parts.length === 2) {
        const id = parseInt(parts[1], 10);
        if (!isNaN(id)) {
          return id;
        }
      }
    }
  } catch (e) {
    // Not base64, continue to other methods
  }

  // Otherwise, try to extract from "ModelName:123" format (if already decoded)
  const parts = globalId.split(':');
  if (parts.length === 2) {
    const id = parseInt(parts[1], 10);
    if (!isNaN(id)) {
      return id;
    }
  }

  // Fallback: try to parse the whole string
  return parseInt(globalId, 10) || 0;
}

const OPERATOR_MAP: Record<string, { label: string; types: string[] }> = {
  '!=': {
    label: 'not equals',
    types: [
      'char',
      'text',
      'selection',
      'many2one',
      'integer',
      'float',
      'boolean'
    ]
  },
  '<': { label: 'less than', types: ['integer', 'float', 'date', 'datetime'] },
  '<=': {
    label: 'less than or equal',
    types: ['integer', 'float', 'date', 'datetime']
  },
  '=': {
    label: 'equals',
    types: [
      'char',
      'text',
      'selection',
      'many2one',
      'integer',
      'float',
      'boolean'
    ]
  },
  '>': {
    label: 'greater than',
    types: ['integer', 'float', 'date', 'datetime']
  },
  '>=': {
    label: 'greater than or equal',
    types: ['integer', 'float', 'date', 'datetime']
  },
  ilike: { label: 'contains', types: ['char', 'text'] },
  in: { label: 'in', types: ['many2one', 'many2many', 'selection'] },
  'not ilike': { label: 'does not contain', types: ['char', 'text'] },
  'not in': { label: 'not in', types: ['many2one', 'many2many', 'selection'] }
};

// Allowed many2one fields that support ForeignKeyMultiSelect
const ALLOWED_FOREIGN_KEY_FIELDS = [
  'parent_id',
  'owner_id',
  'secondary_owner_id'
];

// Memoized component for individual condition rows
const FilterConditionRow = React.memo<{
  condition: FilterCondition;
  fields: Field[];
  getOperatorsForField: (fieldName: string) => string[];
  handleConditionChange: (
    id: string,
    key: keyof FilterCondition,
    value: string
  ) => void;
  handleRemoveCondition: (id: string) => void;
  index: number;
  isFirstInGroup: boolean;
}>(
  ({
    condition,
    fields,
    getOperatorsForField,
    handleConditionChange,
    handleRemoveCondition,
    index,
    isFirstInGroup
  }) => {
    const field = fields.find(f => f.name === condition.field);
    const operators = getOperatorsForField(condition.field);

    // Group fields by category (direct vs related) for better organization
    // Note: many2one fields that aren't in ALLOWED_FOREIGN_KEY_FIELDS are already filtered out
    const directFields = fields.filter(
      f => !f.isRelated && f.ttype !== 'one2many' && f.ttype !== 'many2many'
    );
    const relatedFields = fields.filter(f => f.isRelated);

    return (
      <div className="flex items-start gap-2 rounded-md border p-3">
        <div className="flex-1 space-y-2">
          {!isFirstInGroup && (
            <div className="text-xs font-medium text-gray-500">AND</div>
          )}
          <div className="grid grid-cols-[2fr,1.5fr,2fr] gap-2">
            {/* Field Selector */}
            <div className="min-w-0">
              <Select
                onValueChange={value => {
                  handleConditionChange(condition.id, 'field', value);
                }}
                value={condition.field}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  {directFields.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                        Direct Fields
                      </div>
                      {directFields.map(f => (
                        <SelectItem
                          key={f.id}
                          value={f.name}
                        >
                          {f.fieldDescription || f.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {relatedFields.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                        Related Fields
                      </div>
                      {relatedFields.map(f => (
                        <SelectItem
                          key={f.id}
                          value={f.name}
                        >
                          {f.fieldDescription || f.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Operator Selector */}
            <div className="min-w-0">
              <Select
                disabled={!condition.field}
                onValueChange={value => {
                  handleConditionChange(condition.id, 'operator', value);
                }}
                value={condition.operator}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Operator" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map(op => (
                    <SelectItem
                      key={op}
                      value={op}
                    >
                      {OPERATOR_MAP[op]?.label || op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value Input */}
            <div className="min-w-0">
              {field?.ttype === 'many2one' &&
              ALLOWED_FOREIGN_KEY_FIELDS.includes(field.name) ? (
                <ForeignKeyMultiSelect
                  field={field}
                  onChange={values => {
                    // Store as comma-separated string
                    const valueStr = values.length > 0 ? values.join(',') : '';
                    handleConditionChange(condition.id, 'value', valueStr);
                    // Auto-switch to 'in' operator if multiple values selected
                    if (
                      values.length > 1 &&
                      condition.operator !== 'in' &&
                      condition.operator !== 'not in'
                    ) {
                      handleConditionChange(condition.id, 'operator', 'in');
                    }
                  }}
                  placeholder="Search and select..."
                  values={
                    condition.value
                      ? condition.value.split(',').filter(v => v)
                      : []
                  }
                />
              ) : field?.ttype === 'selection' && field.selectionIds ? (
                // Show multi-select if operator is 'in' or 'not in', or if multiple values already selected
                condition.operator === 'in' ||
                condition.operator === 'not in' ||
                (condition.value &&
                  condition.value.split(',').filter(v => v).length > 1) ? (
                  <MultiSelect
                    onValuesChange={values => {
                      // Store as comma-separated string, or empty string if no values
                      const valueStr =
                        values.length > 0 ? values.join(',') : '';
                      handleConditionChange(condition.id, 'value', valueStr);
                      // Auto-switch to 'in' operator if multiple values selected
                      if (
                        values.length > 1 &&
                        condition.operator !== 'in' &&
                        condition.operator !== 'not in'
                      ) {
                        handleConditionChange(condition.id, 'operator', 'in');
                      }
                    }}
                    values={
                      condition.value
                        ? condition.value.split(',').filter(v => v)
                        : []
                    }
                  >
                    <MultiSelectTrigger className="w-full">
                      <MultiSelectValue placeholder="Select value(s)" />
                    </MultiSelectTrigger>
                    <MultiSelectContent>
                      <MultiSelectGroup>
                        {field.selectionIds.map(opt => (
                          <MultiSelectItem
                            key={opt.value}
                            value={opt.value}
                          >
                            {opt.name}
                          </MultiSelectItem>
                        ))}
                      </MultiSelectGroup>
                    </MultiSelectContent>
                  </MultiSelect>
                ) : (
                  <Select
                    onValueChange={value => {
                      handleConditionChange(condition.id, 'value', value);
                    }}
                    value={condition.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      {field.selectionIds.map(opt => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                        >
                          {opt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              ) : field?.ttype === 'boolean' ? (
                <Select
                  onValueChange={value => {
                    handleConditionChange(condition.id, 'value', value);
                  }}
                  value={condition.value}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select value" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  disabled={!condition.field}
                  onChange={e => {
                    handleConditionChange(
                      condition.id,
                      'value',
                      e.target.value
                    );
                  }}
                  placeholder="Value"
                  type={
                    field?.ttype === 'integer' || field?.ttype === 'float'
                      ? 'number'
                      : 'text'
                  }
                  value={condition.value}
                />
              )}
            </div>
          </div>
        </div>
        <Button
          className="mt-6"
          onClick={() => {
            handleRemoveCondition(condition.id);
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Trash2Icon className="size-4" />
        </Button>
      </div>
    );
  }
);

FilterConditionRow.displayName = 'FilterConditionRow';

export function FilterBuilder({
  onChange,
  targetModel,
  value
}: FilterBuilderProps) {
  const [groups, setGroups] = useState<FilterGroup[]>([
    { conditions: [], id: Math.random().toString() }
  ]);
  const lastGeneratedValue = useRef<string>('');

  // Map model names to Odoo model names
  const odooModel =
    targetModel === 'account' ? 'opstream.account' : 'res.partner';

  const { data, loading } = useQuery(GET_MODEL_FIELDS, {
    skip: !targetModel,
    variables: { model: odooModel }
  });

  // Memoize fields computation to avoid recalculating on every render
  const fields: Field[] = useMemo(() => {
    // Direct fields from the model
    const directFields: Field[] =
      data?.modelFields?.edges.map((e: any) => e.node) || [];

    // Related fields from related models
    const relatedFieldsData: RelatedField[] = data?.relatedModelFields || [];

    // Convert related fields to Field format
    const relatedFields: Field[] = relatedFieldsData.map(rf => ({
      fieldDescription: `${rf.relationLabel} → ${rf.fieldLabel}`,
      id: `related_${rf.fieldPath}`,
      isRelated: true,
      name: rf.fieldPath,
      relation: rf.relatedModel,
      relationLabel: rf.relationLabel,
      selectionIds: rf.selectionOptions?.map(opt => ({
        name: opt,
        value: opt
      })),
      ttype: rf.fieldType
    }));

    // Combine all fields
    const allFields = [...directFields, ...relatedFields];

    // Filter out many2one fields that are not in the allowed list
    return allFields.filter(field => {
      // Keep all non-many2one fields
      if (field.ttype !== 'many2one') {
        return true;
      }
      // For many2one fields, only keep the allowed ones
      return ALLOWED_FOREIGN_KEY_FIELDS.includes(field.name);
    });
  }, [data]);

  // Parse domain into groups
  const parseDomain = useCallback((domain: any[]): FilterGroup[] => {
    if (!Array.isArray(domain) || domain.length === 0) {
      return [{ conditions: [], id: Math.random().toString() }];
    }

    // Check if domain contains OR operators ('|')
    const hasOr = domain.some(item => item === '|');

    if (!hasOr) {
      // Simple case: all conditions are ANDed together
      const conditions: FilterCondition[] = domain
        .filter(item => Array.isArray(item) && item.length === 3)
        .map(item => {
          const operator = item[1];
          let value: string;

          // Handle 'in' and 'not in' operators - value is an array
          if (
            (operator === 'in' || operator === 'not in') &&
            Array.isArray(item[2])
          ) {
            value = item[2].map(v => String(v)).join(',');
          } else {
            value = String(item[2]);
          }

          return {
            field: item[0],
            id: Math.random().toString(),
            operator,
            value
          };
        });

      return [{ conditions, id: Math.random().toString() }];
    }

    // Complex case: parse OR groups
    // Odoo domain format: ['|', [cond1], [cond2]] means cond1 OR cond2
    // Or: ['|', [cond1a, cond1b], cond2] means (cond1a AND cond1b) OR cond2
    // We'll parse this into groups

    // Helper function to parse a condition or array of conditions
    const parseConditionOrGroup = (item: any): FilterCondition[] => {
      if (
        Array.isArray(item) &&
        item.length === 3 &&
        typeof item[0] === 'string'
      ) {
        // It's a single condition [field, operator, value]
        const operator = item[1];
        let value: string;

        // Handle 'in' and 'not in' operators - value is an array
        if (
          (operator === 'in' || operator === 'not in') &&
          Array.isArray(item[2])
        ) {
          value = item[2].map(v => String(v)).join(',');
        } else {
          value = String(item[2]);
        }

        return [
          {
            field: item[0],
            id: Math.random().toString(),
            operator,
            value
          }
        ];
      } else if (Array.isArray(item)) {
        // It's an array of conditions (AND group) or nested OR structure
        const conditions: FilterCondition[] = [];
        for (const subItem of item) {
          if (
            Array.isArray(subItem) &&
            subItem.length === 3 &&
            typeof subItem[0] === 'string'
          ) {
            // It's a condition
            const operator = subItem[1];
            let value: string;

            if (
              (operator === 'in' || operator === 'not in') &&
              Array.isArray(subItem[2])
            ) {
              value = subItem[2].map(v => String(v)).join(',');
            } else {
              value = String(subItem[2]);
            }

            conditions.push({
              field: subItem[0],
              id: Math.random().toString(),
              operator,
              value
            });
          }
        }
        return conditions;
      }
      return [];
    };

    // Recursively parse OR groups
    const parseOrGroups = (domain: any[]): FilterGroup[] => {
      const groups: FilterGroup[] = [];
      let i = 0;

      while (i < domain.length) {
        const item = domain[i];

        if (item === '|') {
          // OR operator - the next items are the operands
          // We need to find where this OR expression ends
          // For ['|', A, B], A and B are the operands
          if (i + 2 < domain.length) {
            const leftOperand = domain[i + 1];
            const rightOperand = domain[i + 2];

            // Parse left operand (could be condition, array of conditions, or nested OR)
            if (
              Array.isArray(leftOperand) &&
              leftOperand.length > 0 &&
              leftOperand[0] === '|'
            ) {
              // Left operand is a nested OR, recurse
              const nestedGroups = parseOrGroups(leftOperand);
              groups.push(...nestedGroups);
            } else {
              const leftConditions = parseConditionOrGroup(leftOperand);
              if (leftConditions.length > 0) {
                groups.push({
                  conditions: leftConditions,
                  id: Math.random().toString()
                });
              }
            }

            // Parse right operand (could be condition, array of conditions, or nested OR)
            if (
              Array.isArray(rightOperand) &&
              rightOperand.length > 0 &&
              rightOperand[0] === '|'
            ) {
              // Right operand is a nested OR, recurse
              const nestedGroups = parseOrGroups(rightOperand);
              groups.push(...nestedGroups);
            } else {
              const rightConditions = parseConditionOrGroup(rightOperand);
              if (rightConditions.length > 0) {
                groups.push({
                  conditions: rightConditions,
                  id: Math.random().toString()
                });
              }
            }

            i += 3; // Skip '|', left, and right operands
          } else {
            i++;
          }
        } else if (
          Array.isArray(item) &&
          item.length === 3 &&
          typeof item[0] === 'string'
        ) {
          // It's a condition (not part of an OR expression)
          const conditions = parseConditionOrGroup(item);
          if (conditions.length > 0) {
            groups.push({
              conditions,
              id: Math.random().toString()
            });
          }
          i++;
        } else if (Array.isArray(item)) {
          // It's an array of conditions (AND group)
          const conditions = parseConditionOrGroup(item);
          if (conditions.length > 0) {
            groups.push({
              conditions,
              id: Math.random().toString()
            });
          }
          i++;
        } else if (item === '&') {
          // AND operator (default), just skip
          i++;
        } else {
          i++;
        }
      }

      return groups;
    };

    const groups = parseOrGroups(domain);

    // If no groups were created, create one empty group
    if (groups.length === 0) {
      return [{ conditions: [], id: Math.random().toString() }];
    }

    return groups;
  }, []);

  // Parse initial value - only when it's different from what we last generated
  useEffect(() => {
    // Skip if this is the same value we just generated (prevents circular updates)
    if (value === lastGeneratedValue.current) {
      return;
    }

    try {
      const domain = JSON.parse(value || '[]');
      const parsedGroups = parseDomain(domain);
      if (parsedGroups.length > 0) {
        setGroups(parsedGroups);
      }
    } catch (e) {
      // Invalid JSON, start fresh
      setGroups([{ conditions: [], id: Math.random().toString() }]);
    }
  }, [value, parseDomain]);

  // Sync groups changes to parent via onChange
  useEffect(() => {
    // Build domain from current groups
    const domainStr = buildDomain(groups);

    // Only call onChange if the domain actually changed from what we last generated
    if (domainStr !== lastGeneratedValue.current) {
      onChange(domainStr);
      lastGeneratedValue.current = domainStr;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Build domain from groups
  const buildDomain = useCallback(
    (groups: FilterGroup[]) => {
      const validGroups = groups.filter(g => {
        return g.conditions.some(c => c.field && c.operator);
      });

      if (validGroups.length === 0) {
        const result = JSON.stringify([], null, 2);
        return result;
      }

      if (validGroups.length === 1) {
        // Single group: just AND all conditions together
        const group = validGroups[0];
        const domain = group.conditions
          .filter(c => {
            const hasField = !!c.field && c.field !== '';
            const hasOperator = !!c.operator && c.operator !== '';
            const hasValue =
              c.value !== undefined && c.value !== null && c.value !== '';
            return hasField && hasOperator && hasValue;
          })
          .map(c => {
            const field = fields.find(f => f.name === c.field);
            let processedValue: any = c.value;

            // Handle 'in' operator - value should be an array
            if (c.operator === 'in' || c.operator === 'not in') {
              // Split comma-separated values into array
              const values = c.value.split(',').filter(v => v.trim());
              processedValue = values.map(v => {
                const trimmed = v.trim();
                // For many2one fields, convert GraphQL global IDs to numeric IDs
                if (field?.ttype === 'many2one') {
                  return extractNumericId(trimmed);
                }
                // Type conversion for array elements
                if (field) {
                  if (field.ttype === 'integer') {
                    return parseInt(trimmed) || 0;
                  } else if (field.ttype === 'float') {
                    return parseFloat(trimmed) || 0.0;
                  }
                }
                return trimmed;
              });
            } else {
              // For many2one fields, convert GraphQL global ID to numeric ID
              if (field?.ttype === 'many2one') {
                processedValue = extractNumericId(c.value);
              } else {
                // Type conversion based on field type for single values
                if (field) {
                  if (field.ttype === 'integer') {
                    processedValue = parseInt(c.value) || 0;
                  } else if (field.ttype === 'float') {
                    processedValue = parseFloat(c.value) || 0.0;
                  } else if (field.ttype === 'boolean') {
                    processedValue = c.value === 'true' || c.value === '1';
                  }
                }
              }
            }

            // Ensure we return a proper 3-element array
            return [c.field, c.operator, processedValue];
          });

        const result = JSON.stringify(domain, null, 2);
        return result;
      }

      // Multiple groups: OR them together
      // Odoo format: ['|', [group1_cond1, group1_cond2], ['|', [group2_cond1], [group3_cond1]]]
      // Multiple conditions in a group are ANDed by default (no '&' needed)

      const buildGroupDomain = (group: FilterGroup): any[] => {
        return group.conditions
          .filter(
            c =>
              c.field && c.operator && c.value !== undefined && c.value !== ''
          )
          .map(c => {
            const field = fields.find(f => f.name === c.field);
            let processedValue: any = c.value;

            // Handle 'in' operator - value should be an array
            if (c.operator === 'in' || c.operator === 'not in') {
              // Split comma-separated values into array
              const values = c.value.split(',').filter(v => v.trim());
              processedValue = values.map(v => {
                const trimmed = v.trim();
                // For many2one fields, convert GraphQL global IDs to numeric IDs
                if (field?.ttype === 'many2one') {
                  return extractNumericId(trimmed);
                }
                // Type conversion for array elements
                if (field) {
                  if (field.ttype === 'integer') {
                    return parseInt(trimmed) || 0;
                  } else if (field.ttype === 'float') {
                    return parseFloat(trimmed) || 0.0;
                  }
                }
                return trimmed;
              });
            } else {
              // For many2one fields, convert GraphQL global ID to numeric ID
              if (field?.ttype === 'many2one') {
                processedValue = extractNumericId(c.value);
              } else {
                // Type conversion based on field type for single values
                if (field) {
                  if (field.ttype === 'integer') {
                    processedValue = parseInt(c.value) || 0;
                  } else if (field.ttype === 'float') {
                    processedValue = parseFloat(c.value) || 0.0;
                  } else if (field.ttype === 'boolean') {
                    processedValue = c.value === 'true' || c.value === '1';
                  }
                }
              }
            }

            // Ensure we return a proper 3-element array
            return [c.field, c.operator, processedValue];
          });
      };

      // Build domain for each group
      const groupDomains = validGroups
        .map(buildGroupDomain)
        .filter(d => d.length > 0);

      if (groupDomains.length === 0) {
        const result = JSON.stringify([], null, 2);
        return result;
      }

      if (groupDomains.length === 1) {
        const result = JSON.stringify(groupDomains[0], null, 2);
        return result;
      }

      // Build OR chain: ['|', group1, ['|', group2, group3]]
      // In Odoo: [cond1, cond2] means cond1 AND cond2
      // ['|', cond1, cond2] means cond1 OR cond2
      // ['|', [cond1a, cond1b], cond2] means (cond1a AND cond1b) OR cond2

      // Helper to normalize a group domain (extract single condition or use array)
      const normalizeGroup = (groupDomain: any[]): any => {
        if (groupDomain.length === 1) {
          return groupDomain[0]; // Single condition: return the condition directly
        }
        return groupDomain; // Multiple conditions: return as array (AND)
      };

      // Start with the last group
      let domain: any = normalizeGroup(groupDomains[groupDomains.length - 1]);

      // Work backwards, wrapping each group with OR
      for (let i = groupDomains.length - 2; i >= 0; i--) {
        const groupDomain = groupDomains[i];
        const normalizedGroup = normalizeGroup(groupDomain);
        domain = ['|', normalizedGroup, domain];
      }

      const result = JSON.stringify(domain, null, 2);
      return result;
    },
    [fields]
  );

  const handleAddCondition = useCallback((groupId: string) => {
    const newCondition: FilterCondition = {
      field: '',
      id: Math.random().toString(),
      operator: '=',
      value: ''
    };
    setGroups(prev => {
      return prev.map(g => {
        if (g.id === groupId) {
          return { ...g, conditions: [...g.conditions, newCondition] };
        }
        return g;
      });
    });
  }, []);

  const handleAddGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      conditions: [],
      id: Math.random().toString()
    };
    setGroups(prev => [...prev, newGroup]);
  }, []);

  const handleRemoveGroup = useCallback((groupId: string) => {
    setGroups(prev => {
      const updated = prev.filter(g => g.id !== groupId);
      // Ensure at least one group exists
      if (updated.length === 0) {
        updated.push({ conditions: [], id: Math.random().toString() });
      }
      return updated;
    });
  }, []);

  const handleRemoveCondition = useCallback(
    (groupId: string, conditionId: string) => {
      setGroups(prev => {
        return prev.map(g => {
          if (g.id === groupId) {
            return {
              ...g,
              conditions: g.conditions.filter(c => c.id !== conditionId)
            };
          }
          return g;
        });
      });
    },
    []
  );

  const handleConditionChange = useCallback(
    (
      groupId: string,
      conditionId: string,
      key: keyof FilterCondition,
      value: string
    ) => {
      setGroups(prev => {
        const updated = prev.map(g => {
          if (g.id === groupId) {
            return {
              ...g,
              conditions: g.conditions.map(c => {
                if (c.id === conditionId) {
                  const updatedCondition = { ...c, [key]: value };

                  // Only reset operator when field changes if current operator is invalid for new field
                  if (key === 'field') {
                    const field = fields.find(f => f.name === value);
                    if (field) {
                      // Get valid operators for the new field
                      const validOperators = Object.entries(OPERATOR_MAP)
                        .filter(([_, config]) =>
                          config.types.includes(field.ttype)
                        )
                        .map(([op]) => op);

                      // Always set operator if it's empty or invalid for this field type
                      if (
                        !updatedCondition.operator ||
                        !validOperators.includes(updatedCondition.operator)
                      ) {
                        // Set default operator based on field type
                        if (field.ttype === 'char' || field.ttype === 'text') {
                          updatedCondition.operator = 'ilike';
                        } else {
                          updatedCondition.operator = '=';
                        }
                      }
                    }
                  }

                  return updatedCondition;
                }
                return c;
              })
            };
          }
          return g;
        });
        return updated;
      });
    },
    [fields]
  );

  const getOperatorsForField = useCallback(
    (fieldName: string) => {
      const field = fields.find(f => f.name === fieldName);
      if (!field) return ['='];

      return Object.entries(OPERATOR_MAP)
        .filter(([_, config]) => config.types.includes(field.ttype))
        .map(([op]) => op);
    },
    [fields]
  );

  if (loading) {
    return <div className="text-sm text-gray-500">Loading fields...</div>;
  }

  if (fields.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No fields found for {targetModel}s. Please select a target model first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Filter Conditions</Label>
        <div className="flex gap-2">
          <Button
            onClick={handleAddGroup}
            size="sm"
            type="button"
            variant="outline"
          >
            <PlusIcon className="mr-1 size-4" />
            Add OR Group
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-sm text-gray-500">
            No filter conditions yet. Click "Add OR Group" to start building
            your filter.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, groupIndex) => (
            <div
              className="rounded-lg border-2 border-gray-200 p-4 dark:border-gray-700"
              key={group.id}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Group {groupIndex + 1}
                  </span>
                  {groups.length > 1 && (
                    <span className="text-xs text-gray-500">
                      (OR with other groups)
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      handleAddCondition(group.id);
                    }}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <PlusIcon className="mr-1 size-4" />
                    Add Condition
                  </Button>
                  {groups.length > 1 && (
                    <Button
                      onClick={() => {
                        handleRemoveGroup(group.id);
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  )}
                </div>
              </div>

              {group.conditions.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-center">
                  <p className="text-sm text-gray-500">
                    No conditions in this group. Click "Add Condition" to add
                    one.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {group.conditions.map((condition, conditionIndex) => (
                    <FilterConditionRow
                      condition={condition}
                      fields={fields}
                      getOperatorsForField={getOperatorsForField}
                      handleConditionChange={(id, key, value) => {
                        handleConditionChange(group.id, id, key, value);
                      }}
                      handleRemoveCondition={() => {
                        handleRemoveCondition(group.id, condition.id);
                      }}
                      index={conditionIndex}
                      isFirstInGroup={conditionIndex === 0}
                      key={condition.id}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500">
        Conditions within a group are combined with AND. Groups are combined
        with OR.
      </div>
    </div>
  );
}
